import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { resolveUrl } from "@/components/ImageUploader";
import { X, Trash2, ShoppingBag, ArrowRight, Plus, Minus } from "lucide-react";
import { Link } from "react-router-dom";

export default function CartDrawer() {
  const { items, open, setOpen, update, remove, slug, count } = useCart();
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    if (!open || items.length === 0) { setPricing(null); return; }
    axios.post(`${API}/public/cart/price`, { tenant_slug: slug, items: items.map(({ sku_variante, qta }) => ({ sku_variante, qta })) })
      .then((r) => setPricing(r.data))
      .catch(() => setPricing(null));
  }, [open, items, slug]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex" data-testid="cart-drawer">
      <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
      <div className="w-full max-w-md bg-white flex flex-col h-full shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="font-heading font-bold text-lg flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" /> Il tuo carrello ({count})
          </div>
          <button data-testid="cart-close" onClick={() => setOpen(false)} className="w-9 h-9 rounded-full hover:bg-accent flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="text-center py-16 text-sm text-muted-foreground">Il carrello è vuoto.</div>
          ) : (
            <div className="space-y-4">
              {(pricing?.items || items).map((it) => (
                <div key={it.sku_variante} className="flex gap-3" data-testid={`cart-item-${it.sku_variante}`}>
                  <div className="w-20 h-20 bg-neutral-100 overflow-hidden flex-shrink-0">
                    {(it.immagine || it.image) && <img src={resolveUrl(it.immagine || it.image)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{it.nome || it.sku_variante}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {Object.values(it.custom_attributes || {}).slice(0, 3).join(" · ")}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-border rounded-sm h-7">
                        <button data-testid={`cart-dec-${it.sku_variante}`} onClick={() => update(it.sku_variante, it.qta - 1)} className="w-7 h-7"><Minus className="w-3 h-3 mx-auto" /></button>
                        <span className="w-7 text-center text-xs font-medium">{it.qta}</span>
                        <button data-testid={`cart-inc-${it.sku_variante}`} onClick={() => update(it.sku_variante, it.qta + 1)} className="w-7 h-7"><Plus className="w-3 h-3 mx-auto" /></button>
                      </div>
                      <div className="text-sm font-medium">€ {it.totale_riga ?? (it.prezzo_unitario || 0) * it.qta}</div>
                    </div>
                  </div>
                  <button data-testid={`cart-remove-${it.sku_variante}`} onClick={() => remove(it.sku_variante)} className="w-7 h-7 text-muted-foreground hover:text-red-700 self-start">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-border p-5 space-y-3">
            {pricing && (
              <>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotale</span><span>€ {pricing.subtotal}</span></div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Spedizione {pricing.shipping_carrier && `(${pricing.shipping_carrier})`}</span>
                  <span>{pricing.shipping === 0 ? "Gratis" : `€ ${pricing.shipping}`}</span>
                </div>
                {pricing.free_shipping_above > 0 && pricing.shipping > 0 && (
                  <div className="text-xs text-muted-foreground">Spedizione gratuita sopra € {pricing.free_shipping_above}</div>
                )}
                <div className="flex justify-between text-base font-heading font-bold pt-2 border-t border-border">
                  <span>Totale</span><span>€ {pricing.total}</span>
                </div>
              </>
            )}
            <button data-testid="cart-continue" onClick={() => setOpen(false)} className="w-full h-10 rounded-sm border border-border text-sm hover:bg-accent">
              Continua gli acquisti
            </button>
            <Link to={`/s/${slug}/checkout`} onClick={() => setOpen(false)} data-testid="cart-checkout-link"
              className="w-full h-12 rounded-sm bg-brand hover:bg-brand-hover text-white text-sm font-medium inline-flex items-center justify-center gap-2">
              Procedi al checkout <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
